import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';

const ProfileContainer = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px;
`;

const Header = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const ProfilePhoto = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 100%;
  object-fit: cover;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const Name = styled.h1`
  margin: 0 0 10px;
  font-size: 2.5rem;
`;

const ContactInfo = styled.div`
  margin: 20px 0;
  
  a {
    color: #007bff;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Bio = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #555;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 40px 0;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: center;
  
  h3 {
    margin: 0;
    font-size: 2rem;
    color: #007bff;
  }
  
  p {
    margin: 10px 0 0;
    color: #666;
  }
`;

const ListingsSection = styled.div`
  margin: 40px 0;
  
  h2 {
    margin-bottom: 20px;
  }
`;

const ListingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const ListingCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
  
  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
  }
  
  .content {
    padding: 20px;
    
    h3 {
      margin: 0 0 10px;
    }
    
    .price {
      font-size: 1.2rem;
      color: #007bff;
      font-weight: bold;
    }
    
    .meta {
      margin: 10px 0;
      color: #666;
    }
  }
`;

const BookButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
  margin-top: 10px;
  font-weight: 500;

  &:hover {
    background: #218838;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #dc3545;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin: 40px auto;
  max-width: 600px;

  h2 {
    margin-bottom: 10px;
    color: #dc3545;
  }

  p {
    color: #666;
    margin-bottom: 20px;
  }

  a {
    color: #007bff;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

function PublicAgentProfile() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!username) {
        setError('Invalid profile URL');
        setLoading(false);
        return;
      }

      try {
        // Convert URL-encoded name back to proper format
        const decodedUsername = decodeURIComponent(username).replace(/-/g, ' ');
        
        console.log('Fetching profile for:', decodedUsername);
        
        const [profileRes, listingsRes] = await Promise.all([
          axios.get(`/api/showingbooker/public/agent/${encodeURIComponent(decodedUsername)}`),
          axios.get(`/api/showingbooker/public/agent/${encodeURIComponent(decodedUsername)}/listings`)
        ]);
        
        console.log('Profile response:', profileRes.data);
        console.log('Listings response:', listingsRes.data);
        
        if (!profileRes.data) {
          setError('Profile not found');
          setLoading(false);
          return;
        }

        setProfile(profileRes.data);
        setListings(listingsRes.data || []);
        setLoading(false);
      } catch (err) {
        console.error('Profile fetch error:', err);
        setError(err.response?.data?.error || 'Failed to load profile');
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username]);

  if (loading) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading profile...</div>
      </ProfileContainer>
    );
  }

  if (error) {
    return (
      <ProfileContainer>
        <ErrorMessage>
          <h2>Unable to Load Profile</h2>
          <p>{error}</p>
          <p>
            Please check the URL and try again, or{' '}
            <a href="/agents">browse all agents</a>
          </p>
        </ErrorMessage>
      </ProfileContainer>
    );
  }

  if (!profile) {
    return (
      <ProfileContainer>
        <ErrorMessage>
          <h2>Profile Not Found</h2>
          <p>The requested agent profile could not be found.</p>
          <p>
            <a href="/agents">Browse all agents</a>
          </p>
        </ErrorMessage>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <Header>
        <ProfilePhoto 
          src={profile.profile_photo_url || 'https://via.placeholder.com/200'} 
          alt={`${profile.first_name} ${profile.last_name}`} 
        />
        <ProfileInfo>
          <Name>{profile.first_name} {profile.last_name}</Name>
          <ContactInfo>
            {profile.phone && <p>📱 <a href={`tel:${profile.phone}`}>{profile.phone}</a></p>}
            {profile.email && <p>📧 <a href={`mailto:${profile.email}`}>{profile.email}</a></p>}
            {profile.license_number && <p>License #: {profile.license_number}</p>}
            {profile.brokerage_name && <p>Brokerage: {profile.brokerage_name}</p>}
          </ContactInfo>
          {profile.bio && <Bio>{profile.bio}</Bio>}
        </ProfileInfo>
      </Header>

      <StatsSection>
        <StatCard>
          <h3>{profile.stats?.active_listings || 0}</h3>
          <p>Active Listings</p>
        </StatCard>
        <StatCard>
          <h3>{profile.stats?.total_bookings || 0}</h3>
          <p>Total Showings</p>
        </StatCard>
        <StatCard>
          <h3>{profile.stats?.monthly?.[0]?.bookings_count || 0}</h3>
          <p>Showings This Month</p>
        </StatCard>
      </StatsSection>

      <ListingsSection>
        <h2>Current Listings</h2>
        {listings.length > 0 ? (
          <ListingsGrid>
            {listings.map(listing => (
              <ListingCard key={listing.id}>
                <img src={listing.photo_url || 'https://via.placeholder.com/300x200'} alt={listing.address_line1} />
                <div className="content">
                  <h3>{listing.address_line1}</h3>
                  {listing.unit_number && <p>Unit {listing.unit_number}</p>}
                  <p>{listing.city}, {listing.state} {listing.zip_code}</p>
                  <p className="price">${listing.price?.toLocaleString()}</p>
                  <p className="meta">{listing.total_bookings} showings booked</p>
                  <BookButton onClick={() => window.location.href = `/book/${listing.id}`}>
                    Book Showing
                  </BookButton>
                </div>
              </ListingCard>
            ))}
          </ListingsGrid>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            No active listings at this time
          </div>
        )}
      </ListingsSection>
    </ProfileContainer>
  );
}

export default PublicAgentProfile;